import {
  getCanonicalShiftStatus,
  getShiftHours,
  isOpenDemandShiftStatus,
  isActiveShiftStatus,
} from "../../../core/shifts/shiftLifecycle";
import { buildShiftMetrics } from "../../../utils/shiftMetricsEngine";
import {
  getShiftFinancialSnapshot,
} from "../../../core/shifts/shiftFinancials";

export function normalizeFacility360Value(
  value
) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

export function facility360ToDate(value) {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  if (typeof value?.seconds === "number") {
    return new Date(value.seconds * 1000);
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? null
    : date;
}

export function getFacility360ShiftStart(
  shift
) {
  return facility360ToDate(
    shift?.startTime ||
      shift?.startAt ||
      shift?.startDateTime ||
      shift?.shiftStart ||
      shift?.start ||
      shift?.date
  );
}

export function getFacility360ShiftEnd(shift) {
  return facility360ToDate(
    shift?.endTime ||
      shift?.endAt ||
      shift?.endDateTime ||
      shift?.shiftEnd ||
      shift?.end
  );
}

export function getFacility360ShiftHours(
  shift
) {
  return getShiftHours(shift);
}

export function getFacility360ShiftRole(
  shift
) {
  return String(
    shift?.role ||
      shift?.requiredPosition ||
      shift?.position ||
      "Other"
  ).toUpperCase();
}

export function shiftBelongsToFacility(
  shift,
  facilityId
) {
  const normalizedFacilityId =
    normalizeFacility360Value(facilityId);

  const shiftFacilityId =
    normalizeFacility360Value(
      shift?.facilityId ||
        shift?.facilityID
    );

  return (
    normalizedFacilityId &&
    shiftFacilityId === normalizedFacilityId
  );
}

export function workerBelongsToFacility(
  worker,
  facilityId
) {
  const normalizedFacilityId =
    normalizeFacility360Value(facilityId);

  if (!normalizedFacilityId) return false;

  const facilityIds = [
    worker?.facilityId,
    worker?.primaryFacilityId,
    worker?.assignedFacilityId,
    ...(Array.isArray(worker?.facilityIds)
      ? worker.facilityIds
      : []),
    ...(Array.isArray(
      worker?.allowedFacilityIds
    )
      ? worker.allowedFacilityIds
      : []),
    ...(Array.isArray(
      worker?.assignedFacilityIds
    )
      ? worker.assignedFacilityIds
      : []),
  ]
    .map(normalizeFacility360Value)
    .filter(Boolean);

  return facilityIds.includes(
    normalizedFacilityId
  );
}

export function getFacility360Shifts(
  facility,
  shifts = []
) {
  const facilityId =
    normalizeFacility360Value(
      facility?.id || facility?.facilityId
    );

  if (!facilityId || !Array.isArray(shifts)) {
    return [];
  }

  return shifts
    .filter((shift) =>
      shiftBelongsToFacility(
        shift,
        facilityId
      )
    )
    .sort((a, b) => {
      const aTime =
        getFacility360ShiftStart(a)?.getTime() ||
        0;

      const bTime =
        getFacility360ShiftStart(b)?.getTime() ||
        0;

      return bTime - aTime;
    });
}

export function getFacility360Workers(
  facility,
  users = []
) {
  const facilityId =
    normalizeFacility360Value(
      facility?.id || facility?.facilityId
    );

  if (!facilityId || !Array.isArray(users)) {
    return [];
  }

  return users.filter((worker) => {
    const operationalRole =
      normalizeFacility360Value(
        worker?.platformRole ||
          worker?.role ||
          worker?.accountType
      );

    return (
      operationalRole === "worker" &&
      workerBelongsToFacility(
        worker,
        facilityId
      )
    );
  });
}

export function getFacility360Rate(
  facility,
  shift,
  rateType
) {
  const shiftRate = Number(
    rateType === "billRate"
      ? shift?.billRate ??
          shift?.facilityBillRate ??
          shift?.rates?.billRate
      : shift?.payRate ??
          shift?.workerPayRate ??
          shift?.rates?.payRate
  );

  if (
    Number.isFinite(shiftRate) &&
    shiftRate > 0
  ) {
    return shiftRate;
  }

  const role =
    getFacility360ShiftRole(shift);

  const facilityRate = Number(
    facility?.rates?.[role]?.[rateType]
  );

  return Number.isFinite(facilityRate) &&
    facilityRate > 0
    ? facilityRate
    : 0;
}

export function buildFacility360Metrics({
  facility,
  shifts = [],
  users = [],
  periodDays = null,
}) {
  const allFacilityShifts =
    getFacility360Shifts(facility, shifts);

  const facilityWorkers =
    getFacility360Workers(facility, users);

  const cutoffDate =
    periodDays &&
    periodDays !== "all" &&
    Number(periodDays) > 0
      ? new Date(
          Date.now() -
            Number(periodDays) *
              24 *
              60 *
              60 *
              1000
        )
      : null;

  const facilityShifts = cutoffDate
    ? allFacilityShifts.filter((shift) => {
        const start =
          getFacility360ShiftStart(shift);

        return (
          start &&
          start.getTime() >=
            cutoffDate.getTime()
        );
      })
    : allFacilityShifts;

const openShifts =
  facilityShifts.filter((shift) =>
    isOpenDemandShiftStatus(
      getCanonicalShiftStatus(shift)
    )
  );

const activeShifts =
  facilityShifts.filter((shift) =>
    isActiveShiftStatus(
      getCanonicalShiftStatus(shift)
    )
  );

const completedShifts =
  facilityShifts.filter(
    (shift) =>
      getCanonicalShiftStatus(shift) ===
      "completed"
  );

const cancelledShifts =
  facilityShifts.filter((shift) =>
    ["cancelled", "canceled"].includes(
      getCanonicalShiftStatus(shift)
    )
  );

const noShowShifts =
  facilityShifts.filter(
    (shift) =>
      getCanonicalShiftStatus(shift) ===
      "no_show"
  );

const closedUnfilledShifts =
  facilityShifts.filter(
    (shift) =>
      getCanonicalShiftStatus(shift) ===
      "closed_unfilled"
  );

 const canonicalShiftMetrics =
  buildShiftMetrics(facilityShifts);

const coverage =
  canonicalShiftMetrics.coverage;

  const completedHours =
    completedShifts.reduce(
      (sum, shift) =>
        sum +
        getFacility360ShiftHours(shift),
      0
    );

  const readyWorkers =
    facilityWorkers.filter((worker) =>
      [
        "ready",
        "active",
        "ready_for_shifts",
      ].includes(
        normalizeFacility360Value(
          worker?.workerOperationalState ||
            worker?.workerStatus ||
            worker?.status
        )
      )
    );

  const compliantWorkers =
    facilityWorkers.filter(
      (worker) =>
        normalizeFacility360Value(
          worker?.complianceStatus
        ) === "approved"
    );

  const pendingWorkers =
    facilityWorkers.filter((worker) =>
      [
        "pending",
        "pending_review",
        "submitted",
        "under_review",
      ].includes(
        normalizeFacility360Value(
          worker?.complianceStatus
        )
      )
    );

  const attentionWorkers =
    facilityWorkers.filter((worker) =>
      [
        "rejected",
        "expired",
        "reupload_required",
        "suspended",
      ].includes(
        normalizeFacility360Value(
          worker?.complianceStatus ||
            worker?.workerStatus ||
            worker?.status
        )
      )
    );

  const workforceCompliance =
    facilityWorkers.length > 0
      ? Math.round(
          (compliantWorkers.length /
            facilityWorkers.length) *
            100
        )
      : 0;

  const roleCounts =
    facilityWorkers.reduce(
      (counts, worker) => {
        const role = String(
          worker?.primaryRole ||
            worker?.position ||
            worker?.workerType ||
            "Other"
        ).toUpperCase();

        counts[role] =
          (counts[role] || 0) + 1;

        return counts;
      },
      {}
    );

  const completedShiftRoleCounts =
    completedShifts.reduce(
      (counts, shift) => {
        const role =
          getFacility360ShiftRole(
            shift
          ).replace(/_/g, " ");

        counts[role] =
          (counts[role] || 0) + 1;

        return counts;
      },
      {}
    );

const financialRows =
  completedShifts.map((shift) => {
    /*
     * Facility 360 may receive older shifts that do not
     * carry their final bill/pay rates directly.
     *
     * Preserve the facility profile rate matrix as a
     * compatibility fallback, but delegate canonical
     * hours and financial math to shiftFinancials.
     */
    const resolvedBillRate =
      getFacility360Rate(
        facility,
        shift,
        "billRate"
      );

    const resolvedPayRate =
      getFacility360Rate(
        facility,
        shift,
        "payRate"
      );

    const snapshot =
      getShiftFinancialSnapshot({
        ...shift,

        billRate:
          Number(shift?.billRate || 0) > 0
            ? shift.billRate
            : resolvedBillRate,

        payRate:
          Number(shift?.payRate || 0) > 0
            ? shift.payRate
            : resolvedPayRate,
      });

    /*
     * Persisted invoice/payroll amounts remain the
     * accounting authority when they already exist.
     */
    const storedRevenue = Number(
      shift?.invoiceAmount ??
        shift?.billAmount ??
        shift?.revenue
    );

    const storedPayroll = Number(
      shift?.payrollAmount ??
        shift?.grossPay ??
        shift?.workerPayAmount
    );

    const revenue =
      Number.isFinite(storedRevenue) &&
      storedRevenue >= 0
        ? storedRevenue
        : snapshot.invoice;

    const payroll =
      Number.isFinite(storedPayroll) &&
      storedPayroll >= 0
        ? storedPayroll
        : snapshot.payroll;

    return {
      shift,

      hours: snapshot.hours,

      billRate:
        snapshot.billRate,

      payRate:
        snapshot.payRate,

      revenue,

      payroll,

      margin:
        Math.round(
          (revenue - payroll) * 100
        ) / 100,
    };
  });

  const financialSummary =
    financialRows.reduce(
      (summary, row) => {
        summary.hours += row.hours;
        summary.revenue +=
          row.revenue;
        summary.payroll +=
          row.payroll;
        summary.margin +=
          row.margin;

        const invoiceStatus =
          normalizeFacility360Value(
            row.shift?.invoiceStatus ||
              "pending"
          );

          if (invoiceStatus === "paid") {
  summary.paidRevenue +=
    row.revenue;
} else {
  summary.pendingRevenue +=
    row.revenue;
}

        return summary;
      },
      {
        hours: 0,
        revenue: 0,
        payroll: 0,
        margin: 0,
        paidRevenue: 0,
        pendingRevenue: 0,
      }
    );

  financialSummary.marginPercent =
    financialSummary.revenue > 0
      ? Math.round(
          (financialSummary.margin /
            financialSummary.revenue) *
            100
        )
      : 0;

  return {
    facilityShifts,
    facilityWorkers,

    openShifts,
    activeShifts,
    completedShifts,
cancelledShifts,
noShowShifts,
closedUnfilledShifts,

    readyWorkers,
    compliantWorkers,
    pendingWorkers,
    attentionWorkers,

    roleCounts,
    completedShiftRoleCounts,

    financialRows,
    financialSummary,

    totalShiftCount:
      facilityShifts.length,

    openCount:
      openShifts.length,

    activeCount:
      activeShifts.length,

    completedCount:
      completedShifts.length,

    cancelledCount:
      cancelledShifts.length,

    noShowCount:
  noShowShifts.length,

closedUnfilledCount:
  closedUnfilledShifts.length,

workforceCount:
      facilityWorkers.length,

    readyWorkerCount:
      readyWorkers.length,

    compliantWorkerCount:
      compliantWorkers.length,

    pendingWorkerCount:
      pendingWorkers.length,

    attentionWorkerCount:
      attentionWorkers.length,

    coverage,
    completedHours,
    workforceCompliance,
  };
}
